/**
 * English strings for the admin screens.
 *
 * Written out in full and derived from nothing — see en/index.ts. Typed as
 * a complete slice, so a key missing here is a build error rather than a
 * screen that quietly renders Bosnian.
 *
 * The reader is an administrator rather than a worker on the floor, so this
 * is allowed to be a little more precise than the sign-in screens — but the
 * two security passages are held to the wording, not the register. A badge
 * is a bearer credential and a photograph of it is enough; an activation
 * code is shown once and is not a password anybody else gets to choose.
 */

import type { AdminSlice } from '../bs/admin'

export const admin: AdminSlice = {
  'admin.users.pager.empty': 'No people',
  // ---- Shell -------------------------------------------------------
  'admin.title': 'Administration',
  'admin.subtitle': 'Users, code lists, 5S templates and the change log',

  'admin.nav.users': 'Users',
  'admin.nav.plants': 'Plants',
  'admin.nav.workCenters': 'Work centres',
  'admin.nav.templates': '5S templates',
  'admin.nav.badges': 'QR stickers',
  'admin.nav.log': 'Log',

  // ---- Verbs and fields the admin screens share --------------------
  'admin.action.cancel': 'Cancel',
  'admin.action.save': 'Save',
  'admin.action.close': 'Close',
  'admin.action.done': 'Done',
  'admin.action.edit.label': 'Edit {name}',
  'admin.action.delete.label': 'Delete {name}',

  'admin.form.editTitle': 'Editing — {name}',
  'admin.saved': 'Your changes have been saved.',
  'admin.delete.failed': 'That could not be deleted.',
  'admin.inactive': 'Inactive',

  'admin.field.name': 'Name',
  'admin.field.code': 'Code',
  'admin.field.plant': 'Plant',
  'admin.field.plant.none': 'No plant',
  'admin.field.sortOrder': 'Order',
  'admin.field.sortOrder.hint': 'The lower the number, the higher up it appears.',

  // ---- Users -------------------------------------------------------
  'admin.users.help':
    'The employee code is what somebody types to sign in and what is printed on their card — it is how the application knows who they are. A new account arrives unusable: saving it hands you a one-time activation code to pass on, and the person then chooses a password only they know.',
  'admin.users.search.placeholder': 'Name, code or e-mail',
  'admin.users.search.label': 'Search users',
  'admin.users.role': 'Role',
  'admin.users.role.all': 'All roles',
  'admin.users.role.explain':
    'The roles that review other people\'s reports, score proposals and award money exist only in KaProLean — KaproMES has no notion of who approves an improvement, so those roles are set here and the sync never touches them.',
  'admin.users.badges': 'Cards',
  'admin.users.new': 'New user',

  'admin.users.column.name': 'Name',
  'admin.users.column.email': 'E-mail',
  'admin.users.column.lastLogin': 'Last sign-in',
  'admin.users.column.actions': 'Actions',

  'admin.users.you': '(you)',
  'admin.users.neverSignedIn': 'Never',
  'admin.users.empty.title': 'No users',
  'admin.users.empty.description': 'Change the search, or add somebody new.',

  'admin.users.action.edit.tip': 'Edit their details',
  'admin.users.action.badge.label': 'QR card for {name}',
  'admin.users.action.badge.tip.reissue': 'A card is out — issue a new one',
  'admin.users.action.badge.tip.issue': 'Issue a QR card',
  'admin.users.action.reset.label': 'Restore access for {name}',
  'admin.users.action.reset.tip': 'Issue a new activation code',
  'admin.users.action.delete.tip.self': 'You cannot delete your own account',
  'admin.users.action.delete.tip': 'Delete the account — the reports stay, but without their author',

  'admin.users.delete.confirm':
    'Delete the user "{name}"?\n\nTheir reports, scores and evaluations stay — they are the plant\'s history — but they no longer show who filed them. If that has to be visible, clear "The account is active" instead of deleting: access goes just as quickly, and the name stays.',
  'admin.users.delete.done': 'The user has been deleted.',

  'admin.users.form.created': 'The user has been created.',
  'admin.users.form.noPassword':
    'You do not set the password. Once you save, you get an activation code to pass on to the worker — they choose their own password when they first sign in.',

  'admin.users.field.name': 'Full name',
  'admin.users.field.name.placeholder': 'e.g. Marko Marković',
  'admin.users.field.code': 'Employee code',
  'admin.users.field.code.hint': 'This is what the user signs in with.',
  'admin.users.field.code.explain':
    'It has to be unique — two people cannot share one; change it later and the person signs in with the new code from then on, while the one printed on their card no longer matches.',
  'admin.users.field.code.placeholder': 'e.g. 1042',
  'admin.users.field.email': 'E-mail',
  'admin.users.field.email.hint': 'Not required.',
  'admin.users.field.email.placeholder': 'first.last@company.com',
  'admin.users.field.phone': 'Phone',
  'admin.users.field.phone.placeholder': 'e.g. 063 123 456',
  'admin.users.field.notify': 'Send notifications by e-mail as well',
  'admin.users.field.notify.hint':
    'Notifications always arrive in the app. This only applies if the user has an e-mail address.',
  'admin.users.field.active': 'The account is active',
  'admin.users.field.active.hint':
    'An inactive user cannot sign in and every session they have open ends at once, but they stay on the list and the reports they filed still carry their name.',

  'admin.users.activation.intro':
    'Give the worker this activation code. It is shown only now — if it is lost, issue a new one.',
  'admin.users.activation.note':
    'It signs them in once, after which they set a password only they know.',
  'admin.users.code.copy': 'Copy',
  'admin.users.code.copied': 'The code has been copied.',

  'admin.users.reset.title': 'Restoring access — {name}',
  'admin.users.reset.issue': 'Issue an activation code',
  'admin.users.reset.explain':
    'The code is good for one sign-in and the account can do nothing else until it is used — the person signs in with it and sets their own password straight away.',
  'admin.users.reset.why':
    'Most workers have no e-mail address, so this is how you hand their access back.',
  'admin.users.reset.note':
    'You are not choosing a password for them — you get a one-time sign-in code, and the worker sets the password.',
  'admin.users.reset.warning': 'Every active session that user has will be ended.',
  'admin.users.reset.codeIntro': 'Give the worker this code. It is shown only now.',
  'admin.users.reset.codeNote':
    'It signs them in once, after which they set a password only they know. Every earlier session of theirs has been ended.',
  'admin.users.issue.failed': 'That could not be issued.',

  'admin.users.badge.title': 'QR card — {name}',
  'admin.users.badge.print': 'Print the card',
  'admin.users.badge.reissue': 'Issue a new card',
  'admin.users.badge.issue': 'Issue a card',
  'admin.users.badge.explain':
    'Everybody has exactly one card that works, so issuing another kills the one in their pocket, at once — which is how a lost card is revoked.',
  'admin.users.badge.why':
    'The worker scans a card instead of typing a code and a password — quicker when you are stood at a machine wearing gloves.',
  'admin.users.badge.printHint':
    'Print it and hand it to the worker. Scanning the card with a phone opens the app already signed in.',
  'admin.users.badge.managerWarning':
    'This is not a worker account. The card is the credential itself — a photograph of it is enough to sign in. For managers and administrators, switch two-factor protection on as well.',
  'admin.users.badge.alreadyIssued': 'A card has already been issued.',
  'admin.users.badge.alreadyIssuedAt': 'A card has already been issued — {when}.',
  'admin.users.badge.revoke': 'Revoke',
  'admin.users.badge.revoke.failed': 'That could not be revoked.',

  // ---- Work centres ------------------------------------------------
  'admin.workCenters.new': 'New work centre',
  'admin.workCenters.noPlants.title': 'Add a plant first',
  'admin.workCenters.noPlants.description':
    'Every work centre belongs to one plant, and none has been created yet.',
  'admin.workCenters.noPlants.action': 'Open plants',
  'admin.workCenters.count.one': '{count} work centre',
  'admin.workCenters.count.many': '{count} work centres',
  'admin.workCenters.typeLabel': 'type: {type}',
  'admin.workCenters.noQr': 'No QR code',
  'admin.workCenters.noQr.explain':
    'This machine has no QR code yet, so a worker cannot scan it — only pick it from the list. Create it with "Reissue" under "QR stickers", and print it there.',
  'admin.workCenters.formLabel': 'template: {name}',
  'admin.workCenters.noForm': 'No 5S template',
  'admin.workCenters.noForm.explain':
    'This work centre cannot be assessed for 5S: no active template exists for type "{type}". Create one with that type under "5S templates".',
  'admin.workCenters.action.remove.label': 'Remove {name}',
  'admin.workCenters.empty': 'No work centres',
  'admin.workCenters.delete.confirm': 'Delete the work centre "{name}"?',
  'admin.workCenters.created': 'The work centre has been created. Print its QR sticker.',

  'admin.workCenters.noTemplates':
    'A new work centre cannot be created until an active 5S template exists for its type — the type is chosen from the list of templates, and none has been written yet. Open "5S templates" and write the first one.',

  'admin.workCenters.field.name.placeholder': 'e.g. Planing',
  'admin.workCenters.field.code.hint':
    'Capitals, digits, hyphen. Used in URLs and on the sticker.',
  'admin.workCenters.field.code.placeholder': 'e.g. HOB-01',
  'admin.workCenters.field.type': '5S template',
  'admin.workCenters.field.type.hint':
    'The work centre is assessed against the template you choose here. If it is not in the list, create it first under "5S templates".',
  'admin.workCenters.field.type.explain':
    'Several work centres can share one template, and a change to it applies to all of them. If this one needs criteria of its own, create a new template for it under "5S templates" and choose it here.',
  'admin.workCenters.field.type.placeholder': 'Choose a 5S template',
  'admin.workCenters.field.type.noMatch':
    'No template has that name or type. New templates are created under "5S templates".',
  'admin.workCenters.field.type.unused': 'no work centre uses it yet',
  'admin.workCenters.field.type.orphan':
    'Type "{type}" has no active 5S template, so this work centre cannot be assessed at the moment.',
  'admin.workCenters.field.type.ownForm':
    'This work centre has its own template, "{name}", and is assessed against it whatever is chosen here.',
  'admin.workCenters.field.description': 'Description',
  'admin.workCenters.field.description.placeholder':
    'Optional — what the centre is for, where it sits in the plant.',
  'admin.workCenters.field.active': 'The work centre is active',
  'admin.workCenters.field.active.hint':
    'An inactive centre is not offered when reporting or when running a 5S evaluation.',

  // ---- Plants ------------------------------------------------------
  'admin.plants.subtitle': 'The company\'s sites. Every work centre belongs to exactly one plant.',
  'admin.plants.help':
    'The hierarchy is company → plant → work centre: every report and every 5S evaluation hangs off a work centre, and every work centre off exactly one plant. So a plant exists before the work centres it holds. Deleting a plant is not removing one site — every work centre in it goes with it, permanently, along with everything reported and evaluated on them.',
  'admin.plants.new': 'New plant',
  'admin.plants.empty.title': 'No plants',
  'admin.plants.empty.description': 'Add your first plant so you can create work centres.',
  'admin.plants.workCenterCount': 'Work centres',
  'admin.plants.userCount': 'Users',
  'admin.plants.edit': 'Edit',
  'admin.plants.delete.tip': 'Delete the plant',
  'admin.plants.deleted': 'The plant has been deleted.',
  'admin.plants.created': 'The plant has been created.',
  'admin.plants.updated': 'The plant has been changed.',
  'admin.plants.form.description': 'A company site, holding the work centres.',
  'admin.plants.field.code.hint': 'Capitals and digits. Appears in reports and on stickers.',
  'admin.plants.field.address': 'Address',

  // ---- 5S templates ------------------------------------------------
  'admin.templates.help':
    'The templates are yours — you enter new work places and new criteria yourself, without us. The order is not arbitrary: first the template (a name, the work centre type and the scale), then five categories, then the criteria inside them, and only then the work centre of that type. A work centre cannot be saved until its type has an active template.',
  'admin.templates.empty': 'No 5S templates',
  'admin.templates.meta':
    'type: {type} · scale 1–{scale} · {items} criteria · {evaluations} evaluations',
  'admin.templates.stats': '{items} criteria · {evaluations} evaluations',
  'admin.templates.locked': 'Locked',
  'admin.templates.newVersion': 'New version',
  'admin.templates.newVersion.hint':
    'Copies the template as the next version and archives this one — earlier evaluations stay attached to this version and their scores do not change, while new ones are scored against the new version.',
  'admin.templates.locked.noAdd':
    'Categories and criteria cannot be added, because earlier evaluations were scored against this template. A category nobody has ever been scored under can still be deleted — there is no score that would change. Everything else goes through "New version" at the top of the card.',
  'admin.templates.lockedNote':
    'This template has been used for submitted evaluations, so the criterion text and the scale can no longer be changed — an edit would retroactively change what earlier scores meant. To change it, create a new version.',
  'admin.templates.newVersion.confirm':
    'Create version {next} of the "{name}" template?\n\nVersion {current} is archived, but earlier evaluations stay attached to it and their scores do not change. New evaluations use the new version.',
  'admin.templates.newVersion.failed': 'The version could not be created.',
  'admin.templates.archived.title': 'Archived versions',
  'admin.templates.archived.note':
    'Earlier evaluations are attached to these versions and their scores stay unchanged.',

  'admin.templates.criterion': 'Criterion',
  'admin.templates.criterion.edit': 'Edit',
  'admin.templates.criterion.edit.label': 'Edit: {criterion}',
  'admin.templates.criterion.remove.label': 'Remove: {criterion}',
  'admin.templates.criterion.remove.hint':
    'A criterion something has already been scored against is deactivated rather than deleted — it stays here struck through so that earlier evaluations remain readable.',
  'admin.templates.criterion.delete.confirm': 'Remove the criterion "{criterion}"?',
  'admin.templates.criterion.editTitle': 'Editing a criterion',
  'admin.templates.criterion.newTitle': 'New criterion',
  'admin.templates.criterion.hint':
    'The text the assessor reads on the floor — it should match the printed form.',
  'admin.templates.criterion.placeholder':
    'e.g. No unnecessary equipment, tools or materials at the workstation',
  'admin.templates.criterion.saved': 'Saved.',

  'admin.templates.guidance': 'Guidance',
  'admin.templates.guidance.hint':
    'Optional help for the assessor. Can be changed even on a locked template.',
  'admin.templates.guidance.placeholder': 'e.g. Check under the bench and behind the machine too.',

  'admin.templates.category.add': 'New category',
  'admin.templates.category.submit': 'Add',
  'admin.templates.category.name.hint': 'For example: Sort, Set in order, Shine.',
  'admin.templates.category.name.placeholder': 'e.g. Sort',
  'admin.templates.category.added': 'The category has been added.',
  'admin.templates.category.failed': 'That could not be added.',
  'admin.templates.category.delete.label': 'Delete category: {name}',
  'admin.templates.category.delete.confirm': 'Delete the category "{name}"?',
  'admin.templates.category.delete.warning':
    'It goes with the criteria inside it ({count}). If any of them have been scored, the deletion is refused — those scores would be left with no criterion to explain what they mean.',

  'admin.templates.settings': 'Edit checklist',
  'admin.templates.settings.saved': 'The checklist has been saved.',
  'admin.templates.meta.machine':
    'checklist for one machine · scale 1–{scale} · {items} criteria · {evaluations} evaluations',
  'admin.templates.machine.badge': 'One machine',
  'admin.templates.machine.carveOut': 'Checklist for one machine',
  'admin.templates.machine.carveOut.submit': 'Create',
  'admin.templates.machine.carveOut.hint':
    'Copies this checklist for a single machine — the original and the other machines of the same type are unchanged, and you can edit the copy straight away.',
  'admin.templates.machine.carveOut.none':
    'No active work centre uses this type, so there is no machine to carve out of it.',
  'admin.templates.machine.carveOut.note':
    'The machine is scored against its own copy from now on, rather than against the type checklist. The other machines of that type do not change. You can give it back to the type checklist whenever you want.',
  'admin.templates.machine.field': 'Work centre',
  'admin.templates.machine.field.placeholder': 'Choose a work centre…',
  'admin.templates.machine.field.hint':
    'The machines this checklist covers today — you are carving one of them out.',
  'admin.templates.machine.detach': 'Give back to the type checklist',
  'admin.templates.machine.detach.hint':
    'The machine is scored against its type checklist again; if anything was scored against this copy it is archived rather than deleted, so earlier evaluations stay readable.',
  'admin.templates.machine.detach.confirm': 'Give "{name}" back to the type checklist?',
  'admin.templates.machine.detach.warning':
    'The criteria written for this machine alone will no longer be used. If the machine’s type has no active checklist, the machine cannot be evaluated for now — you will be warned if that is the case.',

  // ---- Badge sheet -------------------------------------------------
  'admin.badgeSheet.title': 'Cards to print',
  'admin.badgeSheet.print': 'Print ({count})',
  'admin.badgeSheet.issue': 'Issue cards ({count})',
  'admin.badgeSheet.intro':
    'Choose the workers. Each gets their own card, which they scan instead of typing a code and a password.',
  'admin.badgeSheet.printHint':
    'Print on A4, cut along the dashed line and laminate. The cards are shown only now — once you close this window they are gone.',
  'admin.badgeSheet.empty.title': 'No workers',
  'admin.badgeSheet.empty.description':
    'Cards are only issued to workers, and none has been added yet.',
  'admin.badgeSheet.item.inactive': 'inactive',
  'admin.badgeSheet.item.hasBadge': 'already has a card',
  'admin.badgeSheet.truncated':
    'Showing the first {shown} of {total}. Issue the rest from the user list.',
  'admin.badgeSheet.reissue.one':
    'One selected worker already has a card — the old one stops working.',
  'admin.badgeSheet.reissue.many':
    '{count} selected workers already have cards — the old ones stop working.',
  'admin.badgeSheet.reissue.confirm.one':
    '{count} worker already has a card.\n\nIssuing a new one stops the card they are carrying at once. You will have to hand them the new one.',
  'admin.badgeSheet.reissue.confirm.many':
    '{count} workers already have a card.\n\nIssuing new ones stops the cards they are carrying at once. You will have to hand them the new ones.',
  'admin.badgeSheet.partial': '{message} Issued: {done} of {total}.',
  'admin.badgeSheet.failed': 'Not every card could be issued.',

  // ---- Activation codes for a whole shift --------------------------
  // A card can be reprinted from the user list; a code cannot be recovered from
  // anywhere, because it is hashed on the row exactly as a password is. "Shown
  // only now" is literal and has to stay that plain.
  'admin.activation.action': 'Activation codes',
  'admin.activation.title': 'Activation codes to print',
  // "Do not have a password of their own", not "have never signed in":
  // `must_set_password` also covers somebody whose access an administrator took
  // back, and they have signed in plenty.
  'admin.activation.intro':
    'These people do not have a password of their own yet. Each one gets a single-use code to sign in with, and then chooses a password only they know.',
  'admin.activation.issue': 'Issue codes ({count})',
  'admin.activation.print': 'Print ({count})',
  'admin.activation.printHint':
    'Print on A4, cut along the dashed line and hand each person their slip. The codes are shown only now — once you leave this screen they are gone.',
  'admin.activation.empty.title': 'Everybody has their own password',
  'admin.activation.empty.description':
    'No active account is waiting for an activation code. When somebody loses their password, you issue them a code one at a time from the user list.',
  'admin.activation.truncated':
    'Showing the first {shown} of {total}. Codes are issued to everybody waiting, not only to those listed.',
  'admin.activation.reissue':
    'If somebody was issued a code they have not used yet, that code stops working at once — hand them the new slip.',
  'admin.activation.warning':
    'The codes are stored hashed, so after this they cannot be read anywhere. Print or copy them down before you leave this screen; if they are lost, issue new ones.',
  'admin.activation.slip.employeeCode': 'Employee code',
  'admin.activation.slip.code': 'Activation code',
  'admin.activation.slip.instructions':
    'Sign in with your employee code and this code, then set your own password — the code stops working after that.',

  // ---- QR stickers on the machines ---------------------------------
  'admin.stickers.help':
    'Regenerating a code kills every sticker already on that machine: from that moment the old code leads nowhere, and whoever scans it is told the code is unknown and has to pick the work centre by hand. Only regenerate when a sticker is damaged or when the old code must stop working, and print and stick the replacement straight away.',

  // ---- Activity log ------------------------------------------------
  'admin.log.help':
    'The log records changes to reports and evaluations by itself, field by field, with the old value beside the new one; on top of that the admin screens write down what is not an edit at all — an activation code issued, a card issued or revoked, a new template version, a regenerated QR code, a changed currency. Reading is not recorded: the log answers who changed what, not who saw what.',
  'admin.log.search.placeholder': 'Search the descriptions',
  'admin.log.search.label': 'Search the log',
  'admin.log.clearFilters': 'Clear ({count})',
  'admin.log.filter.subject': 'What was changed',
  'admin.log.filter.subject.all': 'All items',
  'admin.log.filter.event': 'Kind of change',
  'admin.log.filter.event.all': 'All changes',
  'admin.log.filter.causer': 'Who changed it',
  'admin.log.filter.causer.all': 'All users',
  'admin.log.filter.logName': 'Kind of entry',
  'admin.log.filter.logName.all': 'All kinds',
  'admin.log.filter.from': 'From date',
  'admin.log.filter.to': 'To date',
  'admin.log.empty.title': 'No entries',
  'admin.log.empty.description':
    'The log fills up as users change reports, evaluations and code lists.',
  'admin.log.subject.fallback': 'Entry',
  'admin.log.causer': '{name} ({code})',
  'admin.log.causer.system': 'System',
  'admin.log.impersonated': 'Support — {name}',
  'admin.log.change.arrow': 'changed to',
  'admin.log.pager.range': '{from}–{to} of {total}',
  'admin.log.pager.perPage.label': 'Entries per page',
  'admin.log.pager.perPage': '{count} per page',
  'admin.log.pager.prev': 'Previous page',
  'admin.log.pager.next': 'Next page',

  // Rows the MES sync owns. See SyncedFromBadge for why the reason is
  // spelled out rather than the field merely being greyed.
  'admin.synced.tooltip': 'This record is maintained in {system}. Change it there.',
  'admin.synced.lastSyncedAt': 'Last synced: {at}',
  'admin.synced.fieldLocked': 'Maintained in {system} — the next sync would undo the change.',

  // The defect list above the templates — see pages/admin/TemplateGaps.tsx.
  'admin.templates.gaps.title': 'Work centres with no 5S checklist',
  'admin.templates.gaps.body': '{types} work centre types have no active checklist — {machines} machines cannot be evaluated.',
  'admin.templates.gaps.machines': '{count} machines',
  'admin.templates.gaps.source': 'Checklist to apply',
  'admin.templates.gaps.source.placeholder': 'Choose a checklist…',
  'admin.templates.gaps.apply': 'Apply to {count} types',
  'admin.templates.gaps.skipped': 'Skipped (already have one): {types}',
  'admin.templates.gaps.note': 'The copy is a separate checklist — the original stays active and unchanged. You can edit the criteria afterwards.',
  'admin.templates.gaps.help':
    'Nobody can run a 5S walk at these machines: their type has no active checklist, so the application has nothing to show. Tick the types, choose the existing checklist that fits them best and apply it — or write a new checklist for each type.',
  'admin.templates.gaps.apply.hint':
    'Copies every category and criterion of the chosen checklist onto each ticked type; a type that already has an active checklist is skipped rather than overwritten.',
  'admin.templates.search.placeholder': 'Search by checklist, type or machine…',
  'admin.templates.search.label': 'Search checklists',
  'admin.templates.search.none': 'No checklist matches "{term}".',
  'admin.templates.moreCentres': '+{count} more',
  'admin.templates.noCentres': 'No work centre is assessed against this template yet.',
  'admin.templates.addCentre': 'Add a work centre',
  'admin.templates.addCentre.hint':
    'Opens the new work centre form with this template already chosen. To move an existing work centre onto it, edit it under "Work centres" — except one synced from KaproMES, whose type is changed in KaproMES.',
  'admin.nav.integration': 'Integration',
  'admin.nav.settings': 'Settings',
  'admin.mes.title': 'MES integration',
  'admin.mes.subtitle': 'Connect KaProLean to your MES — plants, work centres and workers are imported nightly.',
  'admin.mes.help':
    'Everything flows one way: KaproMES owns the plants, work centres and people, so the fields it holds cannot be changed here — an edit would stand until the nightly sync and then vanish. The sync deletes nothing: a work centre or a person that stops appearing in the response is deactivated, because a row missing from a response is far more often a broken connection or a filter than a machine that was genuinely scrapped. A synced person cannot sign in until they set their own password — we do not take password hashes and do not ask for them.',
  'admin.mes.roles.explain':
    'Only the administrator role crosses over, and only upwards: somebody who administers KaproMES becomes an administrator here at the next run, while losing it there does not take it away here — KaproMES does not know which roles decide on proposals and awards, so writing roles downwards would wipe them every night.',
  'admin.mes.report.explain':
    'The report after a run lists both the administrators it promoted and any KaproMES role we do not recognise — such an account still syncs, its role is simply left alone.',
  'admin.mes.sync.plants': 'Plants',
  'admin.mes.sync.workCenters': 'Work centres',
  'admin.mes.sync.workers': 'Workers',
  'admin.mes.sync.deactivated': 'Deactivated',
  'admin.mes.sync.adopted': 'Existing rows claimed',
  'admin.mes.sync.activations':
    'The sync opened {count} new accounts and issued each one an activation code.',
  'admin.mes.sync.clean': 'The sync ran with no problems.',
  'admin.mes.sync.problems': 'The sync ran, but these need attention ({count}):',
  'admin.workCenters.search': 'Search by name, code, type or template…',
  'admin.plants.search': 'Search by name, code or address…',
  'admin.workCenters.delete.warning': 'Every submission, 5S evaluation and attachment for this work centre is permanently deleted too. This cannot be undone.',
  'admin.workCenters.delete.action': 'Delete everything',
  'admin.templates.new': 'New 5S checklist',
  'admin.templates.new.done': 'Checklist created. Now add categories and criteria.',
  'admin.templates.field.type': 'Work centre type',
  'admin.templates.field.type.hint':
    'The checklist applies to every work centre of this type. The types your machines already use are suggested; you can also type one that does not exist yet.',
  'admin.templates.field.type.hint.empty':
    'The checklist applies to every work centre of this type. Type it the way you call it on the floor.',
  'admin.templates.field.type.explain':
    'The type links the template to its work centres: every work centre of this type is assessed against this template. Nobody needs to remember it — a new work centre picks its template by name.',
  'admin.templates.field.type.option.gap.one': 'no checklist · {count} machine',
  'admin.templates.field.type.option.gap.few': 'no checklist · {count} machines',
  'admin.templates.field.type.option.gap.many': 'no checklist · {count} machines',
  'admin.templates.field.type.option.covered': 'already has an active checklist',
  'admin.templates.field.scale': 'Maximum score',
  'admin.templates.field.scale.hint': 'Usually 5 — that is how many steps of score each criterion has.',
  'admin.templates.field.scale.locked':
    'The scale cannot be changed, because evaluations have already been submitted against this checklist and every percentage was computed from it. Create a new version with "New version" at the top of the card, and set the new scale there.',
  'admin.templates.field.notes': 'Note',
  'admin.templates.field.notes.hint':
    'A note for administrators — the evaluator does not see it on the checklist.',
  'admin.templates.field.active': 'This checklist is in use',
  'admin.templates.field.active.hint':
    'Switch it off and work centres of this type are left with no checklist, and cannot be evaluated until you make another.',
  'admin.templates.field.scale.explain':
    'A 1–5 scale gives the assessor five steps and a finer picture of the state of things, while 1–3 is quicker to fill in on the floor; the number locks once the first evaluation has been submitted, because every percentage already worked out was worked out against it.',
  'admin.plants.delete.confirm': 'Delete plant "{name}"?',
  'admin.plants.delete.warning': 'Every work centre in this plant ({count}) is permanently deleted too, along with their submissions, 5S evaluations and attachments. The people stay, just without a plant.',
  'admin.plants.delete.action': 'Delete everything',
  'admin.badgeSheet.inactive.why': 'The account is deactivated — a card for it would not work.',

  'admin.settings.help':
    'The currency is one for the whole company: it changes the symbol beside every amount — on screen, in the PDF report, in a table and in notifications. Nothing is converted along with it: 500 stays 500 and only gains a new symbol, because the application holds no exchange rate and inventing one would be worse than the symbol changing.',
  'admin.settings.currency.title': 'Currency',
  'admin.settings.currency.explain':
    'The change is written into the activity log with the old currency beside the new one, so later there is an answer to when the amounts became euros.',
  'admin.settings.currency.hint':
    'What the company counts savings, costs and awards in. Applies everywhere — the screen, the PDF, the spreadsheet and the notifications.',
  'admin.settings.currency.label': 'Company currency',
  'admin.settings.currency.warning.one':
    '{count} amount is already recorded in the current currency. After the change it will be shown in the new one.',
  'admin.settings.currency.warning.few':
    '{count} amounts are already recorded in the current currency. After the change they will be shown in the new one.',
  'admin.settings.currency.warning.many':
    '{count} amounts are already recorded in the current currency. After the change they will be shown in the new one.',
  'admin.settings.currency.warningEmpty':
    'No amounts have been recorded yet, so this is the best moment to change it.',
  'admin.settings.currency.noConversion':
    'The numbers are not converted — only the currency shown beside them changes.',
  'admin.settings.currency.saved': 'The currency has been changed.',

  // ---- Shifts ------------------------------------------------------
  'admin.nav.shifts': 'Shifts',
  'admin.shifts.subtitle': 'The shifts your plant works.',
  'admin.shifts.help':
    'Shifts are optional: leave them out and the shift field does not appear on the 5S form at all. Enter them if you want to compare how the work centres are kept from one shift to the next — the evaluator then picks a shift on every walk. A night shift ends before it starts, and that is how you enter it.',
  'admin.shifts.new': 'New shift',
  'admin.shifts.edit': 'Edit',
  'admin.shifts.empty.title': 'No shifts defined',
  'admin.shifts.empty.description':
    'Add your first shift, or leave this empty if the plant works a single shift.',
  'admin.shifts.noWindow': 'No clock window',
  'admin.shifts.walks': '{count} evaluations',
  'admin.shifts.created': 'The shift has been created.',
  'admin.shifts.updated': 'The shift has been updated.',
  'admin.shifts.form.description': 'A name is required; the times are not.',
  'admin.shifts.field.startsAt': 'Starts',
  'admin.shifts.field.endsAt': 'Ends',
  'admin.shifts.field.window.hint': 'A night shift ends before it starts — that is fine.',
  'admin.shifts.field.window.explain':
    'The times only let the form preselect whichever shift is running when a walk is entered; the evaluator can change that with one tap, and a shift with no times is chosen by hand.',
  'admin.shifts.field.sortOrder.hint': 'The order they are worked, not alphabetical.',
  'admin.shifts.field.active': 'Active',
  'admin.shifts.field.active.hint':
    'An inactive shift is not offered on the form, but stays on the walks that already name it.',
  'admin.shifts.delete.confirm': 'Delete the shift “{name}”?',
  'admin.shifts.delete.willDelete': 'Nothing uses this shift, so it will be removed for good.',
  'admin.shifts.delete.willDeactivate':
    'This shift is used by {count} evaluations, so it will be deactivated rather than deleted. The earlier walks keep its name.',
  'admin.shifts.delete.action': 'Continue',

  // ---- Tooltips across Administracija ----
  'admin.users.toolbar.explain':
    '"Activation codes" issues and prints single-use codes for everyone who has no password of their own yet; "Cards" issues and prints the QR cards workers sign in with by scanning.',
  'admin.users.column.role.explain':
    'For a deactivated account this column says "Inactive" instead of the role. The role is kept and comes back as soon as you reactivate the account.',
  'admin.users.column.lastLogin.explain':
    'Recorded at every sign-in — with a password, an activation code or a card. "Never" means nobody has signed in to the account yet; usually the person has not been given their activation code, or has not used it.',
  'admin.users.field.plant.explain':
    'The plant is only a label for where the person is filed — it shows on their profile and in the user count under "Plants", but does not decide which work centres they work on. The KaproMES sync fills it in only when it is empty.',
  'admin.users.field.email.explain':
    'Notifications reach it only when "Send notifications by e-mail as well" is switched on. The person can reset a forgotten password by e-mail on their own only once they have confirmed the address on their profile.',
  'admin.users.field.active.synced':
    'KaproMES maintains this account, and every sync sets it active or inactive from the person\'s status in KaproMES — if they are still active there, deactivating them here only lasts until the next sync.',
  'admin.users.delete.codeKept':
    'The employee code {code} stays taken after deletion — no new account can be given it.',
  'admin.users.reset.badgeStays':
    'A new code does not touch the QR card: it still signs this person in. If the card has been lost or photographed, revoke it as well, with the card button in their row.',
  'admin.users.badge.revoke.explain':
    '"Revoke" kills the card at once and issues no new one — from then on the worker signs in with their code and password. To replace a lost card, use "Issue a new card".',
  'admin.users.badge.closeUnprinted':
    'Close this window before printing and the card stays issued but can never be shown again — issue a new one then.',

  'admin.plants.field.name.explain':
    'The plant name is printed on the QR stickers of its work centres. Stickers already on the machines keep working after a rename; they just carry the old name.',
  'admin.plants.workCenterCount.explain':
    'Active and deactivated work centres are both counted — deleting the plant takes all of them.',
  'admin.plants.userCount.explain':
    'People who have this plant set under "Users", active and deactivated alike. Deleting the plant keeps their accounts, just without a plant.',

  'admin.shifts.subtitle.explain':
    'With shifts defined, a work centre gets one 5S evaluation a day for each shift; without them, one a day.',
  'admin.shifts.field.name.explain':
    'The evaluator picks this name on the 5S form, and the per-shift charts are labelled with it. A rename shows on earlier evaluations too — the name is not stored separately with each one.',
  'admin.shifts.field.endsAt.explain':
    'Enter both times or neither — a start without an end, or an end without a start, cannot be saved. A start equal to the end covers no moment at all, so the form never preselects that shift.',
  'admin.shifts.field.sortOrder.explain':
    'Shifts are offered on the 5S form, and lined up on the charts, in this order. If two shifts overlap in time, the form preselects whichever comes first here.',

  'admin.settings.currency.recorded.explain':
    'Every proposal with an expected or actual saving or cost filled in counts once, however many of those fields it has — plus every award given.',
  'admin.settings.currency.undo.explain':
    'Which is why the change can be undone: choose the old currency and the amounts get their old symbol back. So do any amounts entered in the meantime, even though they were entered in the new one.',

  'admin.mes.active.explain':
    'The sync sets the active state too. A worker is active for as long as they are active in KaproMES — deactivating them here only lasts until the next sync. A work centre from KaproMES cannot be deactivated here at all: it is active for as long as KaproMES returns it.',
  'admin.mes.save.explain':
    '"Test connection" and "Sync now" use the saved settings. After changing the address or the token, press "Save" first and test afterwards.',

  'admin.templates.gaps.body.explain':
    'Only active work centres are counted. A machine with a checklist of its own is left out, even though its type has none.',
  'admin.templates.gaps.source.explain':
    'The number in brackets is how many criteria get copied. Each copy takes the same rating scale and the name "5S — type", which you can change later.',

  'admin.log.search.explain':
    'Search looks only at the text of each entry, which is always in Bosnian — an employee code, say, or "Obrisan" (deleted). Changes to reports and evaluations cannot be found this way; use the filters below for those.',
  'admin.log.filter.event.explain':
    'Only the automatic entries for reports and 5S evaluations have a kind of change. Pick one and the entries the admin screens write — issued codes, cards, deleted users and the like — drop out of the list.',
  'admin.log.filter.logName.explain':
    'The part of the application that wrote the entry, under its internal name: user means users, work_center work centres, five_s 5S evaluations, submission reports, and mes the KaproMES sync.',
  'admin.log.causer.system.explain':
    'An entry with no user behind it: an automatic job wrote it, such as the nightly KaproMES sync — or the person who made it has been deleted since.',
  'admin.log.impersonated.explain':
    'KaProLean support made this change while signed in as this user: the name before it is the account that was used, and this says who was actually at the keyboard.',

  'admin.badgeSheet.intro.explain':
    'Only workers are listed — a card for anybody else is issued one at a time from the user list. Active workers who have no card yet are ticked in advance.',
  'admin.badgeSheet.closeUnprinted':
    'Close this window before printing and the cards stay issued but can never be shown again. Issue them once more — tick those people by hand here, as the list will no longer tick them for you.',
  'admin.activation.intro.explain':
    'Listed is every active account without a password of its own: new ones, ones brought over from KaproMES, and anyone whose access was handed back with a code. Anyone who has already set a password gets no code and keeps it.',
  'admin.activation.lostSlip':
    'A lost slip opens that account until somebody uses it — issue that worker a new code straight away with the key button in their row. Do not run this again: it would cancel the slips of everyone still waiting.',
  'admin.twoFactorRequired.title': 'Two-factor protection is required',
  'admin.twoFactorRequired.body':
    'Your company requires every administrator to have two-factor protection turned on. Until you do, administration is locked — the rest of the app works as usual.',
  'admin.twoFactorRequired.how':
    'You need an authenticator app on your phone, such as Google Authenticator or Microsoft Authenticator. Setting it up takes about a minute.',
  'admin.twoFactorRequired.action': 'Turn it on in your profile',
}
